// Odontograma (033) — catálogo de hallazgos + dibujo vectorial del diagrama en
// el PDF firmado (Riesgo 5 del roadmap de formularios MSP, ver PLAN.md).
// Versión deliberadamente más sobria que la pantalla (grilla de piezas con
// color + símbolo de 1-2 letras, sin el tratamiento visual "premium" de la UI
// interactiva) — mismo dato, dos renderers. Dibuja con primitivas nativas de
// `pdf-lib` (mismo enfoque 100% vectorial que el resto de `layout.ts`), sin
// rasterizar la UI ni agregar una librería nueva — ver precedente de
// `embedPng`/`drawImage` ya probado en producción por
// `packages/web/src/api/lib/firma-avanzada.ts` (dibujo no textual SÍ funciona
// en este `pdf-lib@1.17.1`; se optó por vectorial por consistencia con el
// resto del módulo, no por limitación técnica).
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { GRIS_OSCURO } from './layout';
import {
  contornoDePieza,
  esEstadoSuperficie,
  esSuperficieDental,
  esTemporal,
  hallazgoPintaTodaLaPieza,
  poligonoDeSuperficie,
  simboloBajoPieza,
  simboloOficialDeHallazgo,
  SUPERFICIES_DENTALES,
  trazosDeSimbolo,
  type Punto,
  type SimboloOdontograma,
  type SuperficiesPieza,
} from './odontogramaSimbolos';

/**
 * Leyenda oficial (rojo=hallazgo activo, azul=tratamiento realizado/estado
 * consumado) + "sano" (sin marca). Colores explícitos en la fuente oficial:
 * "extracción indicada (X rojo)", "ausente (X azul)". Para el resto, sin
 * color explícito en la fuente disponible — decisión razonable: "…indicada"/
 * "…necesario"/"…por realizar"/caries = rojo (hallazgo activo, pendiente);
 * "…realizado"/obturado/pérdida (ya consumada, sea por caries o por otra
 * causa) = azul (estado ya resuelto/consumado), mismo criterio que "ausente".
 */
export const CATALOGO_HALLAZGOS_ODONTOGRAMA = {
  sano: { etiqueta: 'Sano', simbolo: '', color: 'neutro' },
  caries: { etiqueta: 'Caries', simbolo: 'C', color: 'rojo' },
  obturado: { etiqueta: 'Obturado', simbolo: 'Ob', color: 'azul' },
  ausente: { etiqueta: 'Ausente', simbolo: 'X', color: 'azul' },
  'extraccion-indicada': { etiqueta: 'Extracción indicada', simbolo: 'X', color: 'rojo' },
  'sellante-necesario': { etiqueta: 'Sellante necesario', simbolo: 'Sl', color: 'rojo' },
  'sellante-realizado': { etiqueta: 'Sellante realizado', simbolo: 'Sl', color: 'azul' },
  'perdida-otra-causa': { etiqueta: 'Pérdida (otra causa)', simbolo: 'P', color: 'azul' },
  'perdida-caries': { etiqueta: 'Pérdida por caries', simbolo: 'Pc', color: 'azul' },
  'protesis-total-indicada': { etiqueta: 'Prótesis total indicada', simbolo: 'PT', color: 'rojo' },
  'protesis-total-realizada': { etiqueta: 'Prótesis total realizada', simbolo: 'PT', color: 'azul' },
  'protesis-fija-indicada': { etiqueta: 'Prótesis fija indicada', simbolo: 'PF', color: 'rojo' },
  'protesis-fija-realizada': { etiqueta: 'Prótesis fija realizada', simbolo: 'PF', color: 'azul' },
  'protesis-removible-indicada': { etiqueta: 'Prótesis removible indicada', simbolo: 'PR', color: 'rojo' },
  'protesis-removible-realizada': { etiqueta: 'Prótesis removible realizada', simbolo: 'PR', color: 'azul' },
  'corona-indicada': { etiqueta: 'Corona indicada', simbolo: 'Co', color: 'rojo' },
  'corona-realizada': { etiqueta: 'Corona realizada', simbolo: 'Co', color: 'azul' },
  'endodoncia-por-realizar': { etiqueta: 'Endodoncia por realizar', simbolo: 'En', color: 'rojo' },
  'endodoncia-realizada': { etiqueta: 'Endodoncia realizada', simbolo: 'En', color: 'azul' },
} as const satisfies Record<string, { etiqueta: string; simbolo: string; color: 'rojo' | 'azul' | 'neutro' }>;

export type CodigoHallazgoOdontograma = keyof typeof CATALOGO_HALLAZGOS_ODONTOGRAMA;

/** Notación FDI, dentición permanente + temporal en el mismo gráfico (mismo layout que la ficha oficial en papel). */
export const FILA_PERMANENTE_SUPERIOR = ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'] as const;
export const FILA_TEMPORAL_SUPERIOR = ['55', '54', '53', '52', '51', '61', '62', '63', '64', '65'] as const;
export const FILA_TEMPORAL_INFERIOR = ['85', '84', '83', '82', '81', '71', '72', '73', '74', '75'] as const;
export const FILA_PERMANENTE_INFERIOR = ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'] as const;

/** Piezas índice OMS para CPO-ceo (16/55, 11/51, 26/65, 36/75, 31/71, 46/85) — mismo listado que PLAN.md, exportado acá para que el cálculo (en hce-chart) y el dibujo compartan una sola fuente de verdad. */
export const PIEZAS_INDICE_OMS_CPO_CEO = ['16', '11', '26', '36', '31', '46'] as const;
export const PIEZAS_INDICE_OMS_CEO = ['55', '51', '65', '75', '71', '85'] as const;

export interface EntradaOdontogramaPdf {
  readonly pieza: string;
  readonly hallazgo?: string;
  /** Grado 1-4 (Miller), independiente del hallazgo. */
  readonly recesion?: number;
  /** Grado 1-4, independiente del hallazgo. */
  readonly movilidad?: number;
  /**
   * Caries (rojo) / obturación (azul) por superficie (V, L, M, D, O). Los datos anteriores a esta captura
   * traen `hallazgo: 'caries' | 'obturado'` de pieza completa y se dibujan con todas las superficies pintadas.
   */
  readonly superficies?: SuperficiesPieza;
}

/** Wire format serializable de `EspecificacionPdf` (modo 'msp') — lista dispersa, solo piezas con algún dato. */
export type DatosOdontogramaPdf = readonly EntradaOdontogramaPdf[];

const COLOR_ROJO = rgb(0xb5 / 255, 0x35 / 255, 0x35 / 255);
const COLOR_AZUL = rgb(0x2b / 255, 0x4c / 255, 0x7a / 255);

function colorDeHallazgo(hallazgo: string | undefined): ReturnType<typeof rgb> | undefined {
  if (!hallazgo) return undefined;
  const entrada = CATALOGO_HALLAZGOS_ODONTOGRAMA[hallazgo as CodigoHallazgoOdontograma] as
    | (typeof CATALOGO_HALLAZGOS_ODONTOGRAMA)[CodigoHallazgoOdontograma]
    | undefined;
  if (!entrada || entrada.color === 'neutro') return undefined;
  return entrada.color === 'rojo' ? COLOR_ROJO : COLOR_AZUL;
}

const ALTO_CAJA = 8;
const ANCHO_CAJA = 13;
const TAM_CAJA_TEXTO = 5.5;
const RADIO_PERMANENTE = 9;
const RADIO_TEMPORAL = 7.5;
const ZONA_ASTERISCO = 9;
const ALTO_NUMERO = 8;
const SEPARACION_FILAS = 5;
const COLOR_LINEA = GRIS_OSCURO;

function puntosAPath(puntos: readonly Punto[], radio: number): string {
  return `${puntos.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${(x * radio).toFixed(2)} ${(y * radio).toFixed(2)}`).join(' ')} Z`;
}

function colorDeEstado(estado: 'caries' | 'obturado'): ReturnType<typeof rgb> {
  return estado === 'caries' ? COLOR_ROJO : COLOR_AZUL;
}

/** Estado de cada superficie: el dato por superficie manda; sin él, la caries/obturación de pieza completa (datos viejos) pinta todas. */
function estadoSuperficies(entrada: EntradaOdontogramaPdf | undefined): SuperficiesPieza {
  const porSuperficie: Partial<Record<(typeof SUPERFICIES_DENTALES)[number], 'caries' | 'obturado'>> = {};
  const crudo: Record<string, unknown> = entrada?.superficies && typeof entrada.superficies === 'object' ? (entrada.superficies as Record<string, unknown>) : {};
  for (const [clave, valor] of Object.entries(crudo)) {
    if (esSuperficieDental(clave) && esEstadoSuperficie(valor)) porSuperficie[clave] = valor;
  }
  if (Object.keys(porSuperficie).length > 0) return porSuperficie;
  const toda = hallazgoPintaTodaLaPieza(entrada?.hallazgo);
  if (!toda) return {};
  return Object.fromEntries(SUPERFICIES_DENTALES.map((sup) => [sup, toda]));
}

/** Dibuja un símbolo oficial centrado en (cx, cy) con "semilado" `radio` (pdf-lib: Y hacia arriba, los trazos vienen con Y hacia abajo). */
export function dibujarSimboloOdontograma(pagina: PDFPage, simbolo: SimboloOdontograma, cx: number, cy: number, radio: number, color: ReturnType<typeof rgb>, font: PDFFont): void {
  for (const trazo of trazosDeSimbolo(simbolo)) {
    if (trazo.t === 'linea') {
      pagina.drawLine({
        start: { x: cx + trazo.a[0] * radio, y: cy - trazo.a[1] * radio },
        end: { x: cx + trazo.b[0] * radio, y: cy - trazo.b[1] * radio },
        thickness: 0.9,
        color,
        dashArray: trazo.punteada ? [1, 1.2] : undefined,
      });
    } else if (trazo.t === 'poli') {
      pagina.drawSvgPath(puntosAPath(trazo.pts, radio), { x: cx, y: cy, borderColor: color, borderWidth: 0.9 });
    } else if (trazo.t === 'circulo') {
      pagina.drawCircle({ x: cx, y: cy, size: trazo.r * radio, borderColor: color, borderWidth: 0.9 });
    } else {
      const tam = radio * 1.7;
      pagina.drawText(trazo.s, { x: cx - font.widthOfTextAtSize(trazo.s, tam) / 2, y: cy - tam * 0.35, size: tam, font, color });
    }
  }
}

/** Pieza con sus 5 superficies (cuadrado para permanentes, círculo para temporales) + símbolo oficial del hallazgo de pieza completa. */
function dibujarPieza(pagina: PDFPage, pieza: string, cx: number, cy: number, entrada: EntradaOdontogramaPdf | undefined, font: PDFFont): void {
  const temporal = esTemporal(pieza);
  const radio = temporal ? RADIO_TEMPORAL : RADIO_PERMANENTE;
  const estados = estadoSuperficies(entrada);
  for (const sup of SUPERFICIES_DENTALES) {
    const estado = estados[sup];
    pagina.drawSvgPath(puntosAPath(poligonoDeSuperficie(pieza, sup), radio), {
      x: cx,
      y: cy,
      color: estado ? colorDeEstado(estado) : undefined,
      borderColor: COLOR_LINEA,
      borderWidth: 0.5,
    });
  }
  pagina.drawSvgPath(puntosAPath(contornoDePieza(temporal), radio), { x: cx, y: cy, borderColor: COLOR_LINEA, borderWidth: 0.8 });

  const color = colorDeHallazgo(entrada?.hallazgo);
  const simbolo = simboloOficialDeHallazgo(entrada?.hallazgo);
  if (!simbolo || !color) return;
  if (simboloBajoPieza(simbolo)) dibujarSimboloOdontograma(pagina, simbolo, cx, cy - radio - 4, 3.2, color, font);
  else dibujarSimboloOdontograma(pagina, simbolo, cx, cy, radio * 0.95, color, font);
}

function caja(pagina: PDFPage, cx: number, yTop: number, valor: number | undefined, font: PDFFont): void {
  pagina.drawRectangle({ x: cx - ANCHO_CAJA / 2, y: yTop - ALTO_CAJA, width: ANCHO_CAJA, height: ALTO_CAJA, borderColor: COLOR_LINEA, borderWidth: 0.5 });
  if (valor) {
    const texto = String(valor);
    pagina.drawText(texto, { x: cx - font.widthOfTextAtSize(texto, TAM_CAJA_TEXTO) / 2, y: yTop - ALTO_CAJA + 2, size: TAM_CAJA_TEXTO, font, color: GRIS_OSCURO });
  }
}

function numero(pagina: PDFPage, pieza: string, cx: number, y: number, font: PDFFont): void {
  pagina.drawText(pieza, { x: cx - font.widthOfTextAtSize(pieza, 6) / 2, y, size: 6, font, color: GRIS_OSCURO });
}

type Disposicion = 'permanente-superior' | 'temporal-superior' | 'temporal-inferior' | 'permanente-inferior';

function altoFila(d: Disposicion): number {
  const permanente = d.startsWith('permanente');
  const diente = (permanente ? RADIO_PERMANENTE : RADIO_TEMPORAL) * 2;
  return diente + ZONA_ASTERISCO + ALTO_NUMERO + (permanente ? 2 * ALTO_CAJA + 2 : 0);
}

/** Dibuja una fila y devuelve la `y` del borde inferior de lo dibujado. Superior: recesión, movilidad, número, diente. Inferior: diente, número, movilidad, recesión (como la ficha). */
function dibujarFila(pagina: PDFPage, d: Disposicion, piezas: readonly string[], xInicio: number, yTop: number, cellAncho: number, font: PDFFont, porPieza: ReadonlyMap<string, EntradaOdontogramaPdf>): number {
  const permanente = d.startsWith('permanente');
  const superior = d.endsWith('superior');
  const radio = permanente ? RADIO_PERMANENTE : RADIO_TEMPORAL;
  for (const [i, pieza] of piezas.entries()) {
    const cx = xInicio + i * cellAncho + cellAncho / 2;
    const entrada = porPieza.get(pieza);
    let y = yTop;
    if (superior) {
      if (permanente) {
        caja(pagina, cx, y, entrada?.recesion, font);
        y -= ALTO_CAJA + 1;
        caja(pagina, cx, y, entrada?.movilidad, font);
        y -= ALTO_CAJA + 1;
      }
      numero(pagina, pieza, cx, y - 6, font);
      y -= ALTO_NUMERO;
      dibujarPieza(pagina, pieza, cx, y - radio, entrada, font);
    } else {
      dibujarPieza(pagina, pieza, cx, y - radio, entrada, font);
      y -= radio * 2 + ZONA_ASTERISCO;
      numero(pagina, pieza, cx, y - 6, font);
      y -= ALTO_NUMERO;
      if (permanente) {
        caja(pagina, cx, y, entrada?.movilidad, font);
        y -= ALTO_CAJA + 1;
        caja(pagina, cx, y, entrada?.recesion, font);
      }
    }
  }
  return yTop - altoFila(d);
}

/** Leyenda: símbolos oficiales del bloque K. */
const LEYENDA_SIMBOLOS: readonly { simbolo: SimboloOdontograma; color: 'rojo' | 'azul'; texto: string }[] = [
  { simbolo: 'asterisco', color: 'rojo', texto: 'Sellante necesario' },
  { simbolo: 'asterisco', color: 'azul', texto: 'Sellante realizado' },
  { simbolo: 'x', color: 'rojo', texto: 'Extracción indicada' },
  { simbolo: 'x', color: 'azul', texto: 'Pérdida por caries' },
  { simbolo: 'x-circulo', color: 'azul', texto: 'Pérdida (otra causa)' },
  { simbolo: 'triangulo', color: 'rojo', texto: 'Endodoncia por realizar' },
  { simbolo: 'triangulo', color: 'azul', texto: 'Endodoncia realizada' },
  { simbolo: 'corona', color: 'azul', texto: 'Corona' },
  { simbolo: 'protesis-total', color: 'azul', texto: 'Prótesis total' },
  { simbolo: 'protesis-fija', color: 'azul', texto: 'Prótesis fija' },
  { simbolo: 'protesis-removible', color: 'azul', texto: 'Prótesis removible' },
  { simbolo: 'ausente', color: 'azul', texto: 'Ausente' },
];

const ALTO_TITULO = 14;
const ALTO_LEYENDA = 48;

/** Espacio vertical a reservar con `cursor.dibujo(ALTO_DIBUJO_ODONTOGRAMA, ...)` — título + 4 filas + leyenda de símbolos, con margen. */
export const ALTO_DIBUJO_ODONTOGRAMA =
  ALTO_TITULO +
  altoFila('permanente-superior') +
  altoFila('temporal-superior') +
  altoFila('temporal-inferior') +
  altoFila('permanente-inferior') +
  3 * SEPARACION_FILAS + 5 +
  ALTO_LEYENDA +
  8;

/** Dibuja la grilla FDI completa (permanente + temporal) sobre una `PDFPage` cruda, entregada por `CursorPdf.dibujo()`. Pura: no toca I/O, solo `pdf-lib`. */
export function dibujarOdontograma(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, datos: DatosOdontogramaPdf): void {
  const porPieza = new Map(datos.map((e) => [e.pieza, e] as const));
  const cellAncho = anchoDisponible / FILA_PERMANENTE_SUPERIOR.length;
  const anchoTemporal = cellAncho * FILA_TEMPORAL_SUPERIOR.length;
  const xTemporal = origen.x + (anchoDisponible - anchoTemporal) / 2;

  let y = origen.yTop - 10;
  pagina.drawText('Odontograma', { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO - 10;

  y = dibujarFila(pagina, 'permanente-superior', FILA_PERMANENTE_SUPERIOR, origen.x, y, cellAncho, fontRegular, porPieza) - SEPARACION_FILAS;
  y = dibujarFila(pagina, 'temporal-superior', FILA_TEMPORAL_SUPERIOR, xTemporal, y, cellAncho, fontRegular, porPieza) - SEPARACION_FILAS;
  y = dibujarFila(pagina, 'temporal-inferior', FILA_TEMPORAL_INFERIOR, xTemporal, y, cellAncho, fontRegular, porPieza) - SEPARACION_FILAS - 5;
  y = dibujarFila(pagina, 'permanente-inferior', FILA_PERMANENTE_INFERIOR, origen.x, y, cellAncho, fontRegular, porPieza);

  y -= 4;
  pagina.drawText('Rojo: patología actual   Azul: tratamiento realizado   Superficie pintada: caries (rojo) / obturación (azul)', { x: origen.x, y: y - 6, size: 6.5, font: fontRegular, color: GRIS_OSCURO });
  y -= 12;
  const columnas = 4;
  const anchoColumna = anchoDisponible / columnas;
  for (const [i, item] of LEYENDA_SIMBOLOS.entries()) {
    const x = origen.x + (i % columnas) * anchoColumna;
    const yItem = y - Math.floor(i / columnas) * 12 - 6;
    dibujarSimboloOdontograma(pagina, item.simbolo, x + 6, yItem + 2, 4, item.color === 'rojo' ? COLOR_ROJO : COLOR_AZUL, fontRegular);
    pagina.drawText(item.texto, { x: x + 14, y: yItem, size: 6, font: fontRegular, color: GRIS_OSCURO });
  }
}
